import type { AudioFeatures, SourceKind } from "../audio/types";
import type { SongAudioFeatures } from "../songGalaxy/audioPlayback";
import type { SongAnalysis } from "../songGalaxy/types";

export type ReactivityMode = "legacy" | "fluid" | "hybrid";
export type MusicQualityMode = "low" | "medium" | "high" | "studio";
export type MusicSourceKind = SourceKind | "song-galaxy" | "offline" | "synthetic" | "silent";
export type MusicEventKind =
  | "beat"
  | "downbeat"
  | "onset"
  | "phraseStart"
  | "buildStart"
  | "drop"
  | "quietBreak"
  | "sectionChange";

export type MusicTimeState = {
  nowMs: number;
  audioTimeSec: number;
  dtSec: number;
  sourceTimeSec?: number;
};

export type MusicSourceState = {
  kind: MusicSourceKind;
  live: boolean;
  hasAudio: boolean;
  confidence: number;
  qualityMode: MusicQualityMode;
  latencyMs?: number;
  trackId?: string;
  label?: string;
};

export type MusicRawSignals = {
  rms: number;
  peak: number;
  spectralFlux: number;
  onsetStrength: number;
  spectrum?: Float32Array;
  waveform?: Float32Array;
};

export type MusicBands = {
  subBass: number;
  bass: number;
  lowMid: number;
  mids: number;
  highMid: number;
  highs: number;
  vocalPresence: number;
  brightness: number;
};

export type MusicRhythm = {
  beat: number;
  beatPhase: number;
  beatCount: number;
  beatConfidence: number;
  tempo: number;
  tempoConfidence: number;
  barPhase: number;
  phrasePhase: number;
  onset: number;
  transient: number;
};

export type MusicMusicalSignals = {
  intensity: number;
  tension: number;
  calm: number;
  buildEnergy: number;
  dropLikelihood: number;
  density: number;
  groove: number;
  motion: number;
  colorEnergy: number;
  brightness: number;
  percussiveStrength: number;
};

export type MusicFluidSignals = {
  breath: number;
  weight: number;
  pressure: number;
  release: number;
  swirl: number;
  turbulence: number;
  current: number;
  shimmer: number;
  drift: number;
  expansion: number;
  cohesion: number;
  elasticity: number;
};

export type MusicEvent = {
  kind: MusicEventKind;
  strength: number;
  atSec?: number;
  confidence?: number;
  index?: number;
  label?: string;
};

export type MusicSmoothedSignals = {
  bands: MusicBands;
  rhythm: Pick<MusicRhythm, "beat" | "beatPhase" | "barPhase" | "phrasePhase" | "onset" | "transient">;
  musical: MusicMusicalSignals;
  fluid: MusicFluidSignals;
};

export type MusicDebugState = {
  legacy?: AudioFeatures;
  sectionLabel?: string;
  analyzer?: Record<string, number | string | boolean>;
};

export type MusicState = {
  version: 1;
  mode: ReactivityMode;
  time: MusicTimeState;
  source: MusicSourceState;
  raw: MusicRawSignals;
  bands: MusicBands;
  rhythm: MusicRhythm;
  musical: MusicMusicalSignals;
  fluid: MusicFluidSignals;
  smoothed: MusicSmoothedSignals;
  events: MusicEvent[];
  debug?: MusicDebugState;
};

export type LegacyMusicStateOptions = {
  mode?: ReactivityMode;
  sourceKind?: MusicSourceKind;
  audioTimeSec?: number;
  sourceTimeSec?: number;
  dtSec?: number;
  confidence?: number;
  qualityMode?: MusicQualityMode;
  label?: string;
};

export type SongMusicStateOptions = {
  songId: string;
  nowMs?: number;
  audioTimeSec?: number;
  dtSec?: number;
  analysis?: SongAnalysis;
  qualityMode?: MusicQualityMode;
};

export type OfflineSectionLike = {
  label?: string;
  start: number;
  end: number;
  energy?: number;
};

export type OfflineAnalysisLike = {
  duration?: number;
  bpm?: number;
  beats?: number[];
  downbeats?: number[];
  energy_times?: number[];
  bass?: number[];
  mid?: number[];
  treble?: number[];
  rms?: number[];
  sections?: OfflineSectionLike[];
  onsets?: number[];
};

export type OfflineMusicStateOptions = {
  timeSec: number;
  nowMs?: number;
  dtSec?: number;
  qualityMode?: MusicQualityMode;
};

const DEFAULT_DT_SEC = 1 / 60;

const clamp01 = (value: number): number => Math.max(0, Math.min(1, Number.isFinite(value) ? value : 0));

const nowMs = (): number => (typeof performance !== "undefined" ? performance.now() : Date.now());

const wrap01 = (value: number): number => {
  if (!Number.isFinite(value)) return 0;
  return ((value % 1) + 1) % 1;
};

const phaseFromTempo = (audioTimeSec: number, bpm: number, beatsPerCycle: number): number => {
  if (bpm <= 0 || beatsPerCycle <= 0) return 0;
  return wrap01((audioTimeSec * bpm) / 60 / beatsPerCycle);
};

const weightedLevel = (bass: number, mids: number, highs: number): number => clamp01(bass * 0.42 + mids * 0.34 + highs * 0.24);

const isLiveSourceKind = (kind: MusicSourceKind): boolean => kind === "mic" || kind === "tab" || kind === "file" || kind === "song-galaxy";

const makeBands = (bass: number, mids: number, highs: number): MusicBands => {
  const cleanBass = clamp01(bass);
  const cleanMids = clamp01(mids);
  const cleanHighs = clamp01(highs);
  return {
    subBass: clamp01(cleanBass * 0.82),
    bass: cleanBass,
    lowMid: clamp01(cleanBass * 0.28 + cleanMids * 0.72),
    mids: cleanMids,
    highMid: clamp01(cleanMids * 0.68 + cleanHighs * 0.32),
    highs: cleanHighs,
    vocalPresence: clamp01(cleanMids * 0.74 + cleanHighs * 0.16),
    brightness: cleanHighs,
  };
};

const makeRhythm = (input: {
  beat: number;
  beatCount?: number;
  bpm?: number;
  audioTimeSec: number;
  beatConfidence?: number;
  onset?: number;
}): MusicRhythm => {
  const tempo = Math.max(0, Number.isFinite(input.bpm ?? 0) ? input.bpm ?? 0 : 0);
  const beat = clamp01(input.beat);
  const onset = clamp01(input.onset ?? beat);
  return {
    beat,
    beatPhase: phaseFromTempo(input.audioTimeSec, tempo, 1),
    beatCount: Math.max(0, Math.floor(input.beatCount ?? 0)),
    beatConfidence: clamp01(input.beatConfidence ?? (beat > 0 ? Math.max(0.35, beat) : 0)),
    tempo,
    tempoConfidence: tempo > 0 ? 0.62 : 0,
    barPhase: phaseFromTempo(input.audioTimeSec, tempo, 4),
    phrasePhase: phaseFromTempo(input.audioTimeSec, tempo, 16),
    onset,
    transient: Math.max(beat, onset),
  };
};

const makeMusical = (bands: MusicBands, rhythm: MusicRhythm, densityHint = 0): MusicMusicalSignals => {
  const intensity = weightedLevel(bands.bass, bands.mids, bands.highs);
  const brightness = bands.brightness;
  const density = clamp01(densityHint || rhythm.onset * 0.45 + bands.highs * 0.35 + intensity * 0.2);
  const calm = clamp01(1 - intensity * 0.72 - rhythm.transient * 0.34 - density * 0.18);
  const buildEnergy = clamp01(intensity * 0.48 + density * 0.34 + rhythm.onset * 0.18);
  const dropLikelihood = clamp01(rhythm.beat * 0.45 + bands.bass * 0.35 + Math.max(0, buildEnergy - calm) * 0.2);
  return {
    intensity,
    tension: clamp01(bands.highMid * 0.35 + density * 0.35 + buildEnergy * 0.3),
    calm,
    buildEnergy,
    dropLikelihood,
    density,
    groove: clamp01(rhythm.beatConfidence * 0.52 + bands.bass * 0.28 + rhythm.beat * 0.2),
    motion: clamp01(intensity * 0.42 + density * 0.28 + bands.mids * 0.3),
    colorEnergy: clamp01(brightness * 0.45 + bands.bass * 0.25 + intensity * 0.3),
    brightness,
    percussiveStrength: clamp01(rhythm.transient * 0.62 + bands.bass * 0.26 + bands.highs * 0.12),
  };
};

const makeFluid = (bands: MusicBands, rhythm: MusicRhythm, musical: MusicMusicalSignals): MusicFluidSignals => ({
  breath: clamp01(musical.calm * 0.55 + musical.intensity * 0.2 + rhythm.barPhase * 0.25),
  weight: clamp01(bands.subBass * 0.38 + bands.bass * 0.62),
  pressure: clamp01(musical.intensity * 0.56 + bands.bass * 0.32 + rhythm.beat * 0.12),
  release: clamp01(rhythm.beat * 0.64 + musical.dropLikelihood * 0.36),
  swirl: clamp01(bands.mids * 0.58 + musical.motion * 0.28 + rhythm.phrasePhase * 0.14),
  turbulence: clamp01(musical.density * 0.52 + rhythm.transient * 0.28 + bands.highs * 0.2),
  current: clamp01(musical.motion * 0.54 + musical.groove * 0.34 + bands.lowMid * 0.12),
  shimmer: clamp01(bands.highs * 0.62 + musical.brightness * 0.26 + rhythm.onset * 0.12),
  drift: clamp01(musical.calm * 0.7 + bands.mids * 0.18 + (1 - rhythm.beatConfidence) * 0.12),
  expansion: clamp01(bands.bass * 0.48 + musical.intensity * 0.32 + rhythm.beat * 0.2),
  cohesion: clamp01(1 - musical.density * 0.48 - rhythm.transient * 0.2),
  elasticity: clamp01(0.28 + musical.groove * 0.35 + musical.calm * 0.22 + bands.mids * 0.15),
});

const makeSmoothed = (
  bands: MusicBands,
  rhythm: MusicRhythm,
  musical: MusicMusicalSignals,
  fluid: MusicFluidSignals,
): MusicSmoothedSignals => ({
  bands,
  rhythm: {
    beat: rhythm.beat,
    beatPhase: rhythm.beatPhase,
    barPhase: rhythm.barPhase,
    phrasePhase: rhythm.phrasePhase,
    onset: rhythm.onset,
    transient: rhythm.transient,
  },
  musical,
  fluid,
});

const makeEvents = (rhythm: MusicRhythm, sectionLabel?: string): MusicEvent[] => {
  const events: MusicEvent[] = [];
  if (rhythm.beat > 0.85) {
    events.push({ kind: "beat", strength: rhythm.beat, confidence: rhythm.beatConfidence, index: rhythm.beatCount });
  }
  if (rhythm.onset > 0.85 && rhythm.beat <= 0.85) {
    events.push({ kind: "onset", strength: rhythm.onset, confidence: rhythm.beatConfidence });
  }
  if (sectionLabel) {
    events.push({ kind: "sectionChange", strength: 1, label: sectionLabel });
  }
  return events;
};

const buildState = (input: {
  mode: ReactivityMode;
  time: MusicTimeState;
  source: MusicSourceState;
  raw: MusicRawSignals;
  bands: MusicBands;
  rhythm: MusicRhythm;
  musical?: MusicMusicalSignals;
  debug?: MusicDebugState;
  sectionLabel?: string;
}): MusicState => {
  const musical = input.musical ?? makeMusical(input.bands, input.rhythm);
  const fluid = makeFluid(input.bands, input.rhythm, musical);
  return {
    version: 1,
    mode: input.mode,
    time: input.time,
    source: input.source,
    raw: input.raw,
    bands: input.bands,
    rhythm: input.rhythm,
    musical,
    fluid,
    smoothed: makeSmoothed(input.bands, input.rhythm, musical, fluid),
    events: makeEvents(input.rhythm, input.sectionLabel),
    debug: input.debug,
  };
};

export function createSilentMusicState(options: Partial<LegacyMusicStateOptions> = {}): MusicState {
  const currentNowMs = nowMs();
  const audioTimeSec = options.audioTimeSec ?? currentNowMs / 1000;
  const bands = makeBands(0, 0, 0);
  const rhythm = makeRhythm({ beat: 0, bpm: 0, audioTimeSec });
  return buildState({
    mode: options.mode ?? "legacy",
    time: {
      nowMs: currentNowMs,
      audioTimeSec,
      dtSec: options.dtSec ?? DEFAULT_DT_SEC,
      sourceTimeSec: options.sourceTimeSec,
    },
    source: {
      kind: options.sourceKind ?? "silent",
      live: false,
      hasAudio: false,
      confidence: clamp01(options.confidence ?? 0),
      qualityMode: options.qualityMode ?? "medium",
      label: options.label,
    },
    raw: { rms: 0, peak: 0, spectralFlux: 0, onsetStrength: 0 },
    bands,
    rhythm,
  });
}

export function fromLegacyAudioFeatures(features: AudioFeatures, options: LegacyMusicStateOptions = {}): MusicState {
  const audioTimeSec = options.audioTimeSec ?? features.t / 1000;
  const bands = makeBands(features.bass, features.mid, features.treble);
  const rhythm = makeRhythm({
    beat: features.beat,
    beatCount: features.beatCount,
    bpm: features.bpm,
    audioTimeSec,
    beatConfidence: features.beat > 0 ? 0.7 : 0,
    onset: features.beat,
  });
  const level = clamp01(features.level);
  return buildState({
    mode: options.mode ?? "legacy",
    time: {
      nowMs: features.t,
      audioTimeSec,
      dtSec: options.dtSec ?? DEFAULT_DT_SEC,
      sourceTimeSec: options.sourceTimeSec,
    },
    source: {
      kind: options.sourceKind ?? "none",
      live: isLiveSourceKind(options.sourceKind ?? "none"),
      hasAudio: level > 0.001 || Boolean(features.spectrum || features.waveform),
      confidence: clamp01(options.confidence ?? (level > 0.001 ? 0.72 : 0)),
      qualityMode: options.qualityMode ?? "medium",
      label: options.label,
    },
    raw: {
      rms: level,
      peak: Math.max(level, features.beat),
      spectralFlux: 0,
      onsetStrength: features.beat,
      spectrum: features.spectrum,
      waveform: features.waveform,
    },
    bands,
    rhythm,
    debug: { legacy: features },
  });
}

export function toLegacyAudioFeatures(state: MusicState): AudioFeatures {
  const bass = clamp01(state.smoothed.bands.bass);
  const mid = clamp01(state.smoothed.bands.mids);
  const treble = clamp01(state.smoothed.bands.highs);
  return {
    bass,
    mid,
    treble,
    level: weightedLevel(bass, mid, treble),
    bpm: state.rhythm.tempo,
    beat: clamp01(state.smoothed.rhythm.beat),
    beatCount: state.rhythm.beatCount,
    t: state.time.nowMs,
    spectrum: state.raw.spectrum,
    waveform: state.raw.waveform,
  };
}

export function fromSongAudioFeatures(features: SongAudioFeatures, options: SongMusicStateOptions): MusicState {
  const currentNowMs = options.nowMs ?? nowMs();
  const audioTimeSec = options.audioTimeSec ?? currentNowMs / 1000;
  const bands = makeBands(features.bass, features.mid, features.treble);
  const rhythm = makeRhythm({
    beat: features.beat,
    bpm: options.analysis?.bpm,
    audioTimeSec,
    beatConfidence: features.playing ? Math.max(0.25, features.volume) : 0,
    onset: features.beat,
  });
  const densityHint = options.analysis?.density ?? features.energy;
  const musical = makeMusical(bands, rhythm, densityHint);
  const analysisBrightness = options.analysis?.brightness;
  const adjustedMusical: MusicMusicalSignals = {
    ...musical,
    brightness: clamp01(analysisBrightness ?? musical.brightness),
    colorEnergy: clamp01((analysisBrightness ?? musical.colorEnergy) * 0.42 + musical.colorEnergy * 0.58),
  };
  return buildState({
    mode: "fluid",
    time: {
      nowMs: currentNowMs,
      audioTimeSec,
      dtSec: options.dtSec ?? DEFAULT_DT_SEC,
    },
    source: {
      kind: "song-galaxy",
      live: true,
      hasAudio: features.playing && features.volume > 0.001,
      confidence: clamp01(features.playing ? features.volume : options.analysis?.analysisAvailable ? 0.35 : 0),
      qualityMode: options.qualityMode ?? "medium",
      trackId: options.songId,
      label: options.analysis?.title,
    },
    raw: {
      rms: clamp01(features.energy),
      peak: Math.max(clamp01(features.energy), clamp01(features.volume), clamp01(features.beat)),
      spectralFlux: 0,
      onsetStrength: clamp01(features.beat),
    },
    bands,
    rhythm,
    musical: adjustedMusical,
  });
}

const sampleCurve = (times: number[] | undefined, values: number[] | undefined, timeSec: number): number => {
  if (!values || values.length === 0) return 0;
  if (!times || times.length !== values.length) {
    const safeIndex = Math.max(0, Math.min(values.length - 1, Math.floor(timeSec)));
    return clamp01(values[safeIndex] ?? 0);
  }
  if (timeSec <= times[0]) return clamp01(values[0] ?? 0);
  const lastIndex = values.length - 1;
  if (timeSec >= times[lastIndex]) return clamp01(values[lastIndex] ?? 0);
  let index = 0;
  while (index < lastIndex && times[index + 1] < timeSec) index += 1;
  const t0 = times[index] ?? 0;
  const t1 = times[index + 1] ?? t0 + 1;
  const v0 = values[index] ?? 0;
  const v1 = values[index + 1] ?? v0;
  const mix = t1 > t0 ? (timeSec - t0) / (t1 - t0) : 0;
  return clamp01(v0 + (v1 - v0) * mix);
};

const envelopeFromTimes = (times: number[] | undefined, timeSec: number, windowSec: number): number => {
  if (!times || times.length === 0) return 0;
  let best = Number.POSITIVE_INFINITY;
  for (const eventTime of times) best = Math.min(best, Math.abs(eventTime - timeSec));
  return best <= windowSec ? clamp01(1 - best / windowSec) : 0;
};

const sectionAtTime = (sections: OfflineSectionLike[] | undefined, timeSec: number): OfflineSectionLike | undefined =>
  sections?.find((section) => timeSec >= section.start && timeSec < section.end);

export function fromOfflineAnalysis(analysis: OfflineAnalysisLike, options: OfflineMusicStateOptions): MusicState {
  const audioTimeSec = Math.max(0, options.timeSec);
  const currentNowMs = options.nowMs ?? audioTimeSec * 1000;
  const bass = sampleCurve(analysis.energy_times, analysis.bass, audioTimeSec);
  const mid = sampleCurve(analysis.energy_times, analysis.mid, audioTimeSec);
  const treble = sampleCurve(analysis.energy_times, analysis.treble, audioTimeSec);
  const rms = sampleCurve(analysis.energy_times, analysis.rms, audioTimeSec) || weightedLevel(bass, mid, treble);
  const beat = envelopeFromTimes(analysis.beats, audioTimeSec, 0.14);
  const onset = Math.max(beat, envelopeFromTimes(analysis.onsets, audioTimeSec, 0.1));
  const activeSection = sectionAtTime(analysis.sections, audioTimeSec);
  const bands = makeBands(bass, mid, treble);
  const rhythm = makeRhythm({
    beat,
    bpm: analysis.bpm,
    audioTimeSec,
    beatConfidence: analysis.beats && analysis.beats.length > 0 ? 0.82 : 0.25,
    onset,
  });
  const musical = makeMusical(bands, rhythm, activeSection?.energy);
  return buildState({
    mode: "fluid",
    time: {
      nowMs: currentNowMs,
      audioTimeSec,
      dtSec: options.dtSec ?? DEFAULT_DT_SEC,
      sourceTimeSec: audioTimeSec,
    },
    source: {
      kind: "offline",
      live: false,
      hasAudio: true,
      confidence: 0.86,
      qualityMode: options.qualityMode ?? "studio",
    },
    raw: {
      rms,
      peak: Math.max(rms, beat, onset),
      spectralFlux: onset,
      onsetStrength: onset,
    },
    bands,
    rhythm,
    musical,
    debug: activeSection?.label ? { sectionLabel: activeSection.label } : undefined,
    sectionLabel: activeSection?.label,
  });
}
